import { SIGNED_IN_COOKIE_NAME } from '../constants'

const EXPIRED_SESSION_KEY = 'pixee.session.expired'
const EXPIRED_SESSION_REDIRECT_PATH_KEY = 'pixee.session.expired-redirect-path'
const EXPIRED_SESSION_RETRIED_KEY = 'pixee.session.expired-logout-retried'

function hasSignedInCookie(): boolean {
  // eslint-disable-next-line no-restricted-properties -- reads a server-set cookie; no React API exposes cookies
  return document.cookie.split(';').some(cookie => cookie.trim().startsWith(`${SIGNED_IN_COOKIE_NAME}=`))
}

// Session storage throws under some privacy settings. Losing the marker costs only the expiry
// message, and a throw here would abort the logout navigation that follows it.
function withSessionStorage<T>(operation: (storage: Storage) => T, fallback: T): T {
  try {
    return operation(sessionStorage)
  } catch {
    return fallback
  }
}

export type ExpiredSession = {
  /** Where the user was when the session lapsed, or `null` if that was not recorded. */
  redirectPath: string | null
}

export function readExpiredSession(): ExpiredSession | null {
  return withSessionStorage(storage => {
    if (storage.getItem(EXPIRED_SESSION_KEY) === null) {
      return null
    }

    return { redirectPath: storage.getItem(EXPIRED_SESSION_REDIRECT_PATH_KEY) }
  }, null)
}

/**
 * Records that the session lapsed, for the login page the browser reaches after logout.
 *
 * Kept in session storage rather than carried on the logout URL because `/api/auth/logout` ignores
 * its query string and Authentik's post-logout flow picks the landing page, so no query parameter
 * survives the round trip.
 */
export function markSessionExpired(redirectPath: string): void {
  withSessionStorage(storage => {
    storage.setItem(EXPIRED_SESSION_KEY, '1')
    storage.setItem(EXPIRED_SESSION_REDIRECT_PATH_KEY, redirectPath)
    storage.removeItem(EXPIRED_SESSION_RETRIED_KEY)
  }, undefined)
}

export function clearExpiredSession(): void {
  withSessionStorage(storage => {
    storage.removeItem(EXPIRED_SESSION_KEY)
    storage.removeItem(EXPIRED_SESSION_REDIRECT_PATH_KEY)
    storage.removeItem(EXPIRED_SESSION_RETRIED_KEY)
  }, undefined)
}

/** Claims the single retry an unfinished logout gets, returning `false` once it has been spent. */
function claimLogoutRetry(): boolean {
  return withSessionStorage(storage => {
    if (storage.getItem(EXPIRED_SESSION_RETRIED_KEY) !== null) {
      return false
    }

    storage.setItem(EXPIRED_SESSION_RETRIED_KEY, '1')
    return true
  }, false)
}

let isEndingSession = false

/**
 * Decides, once per document, whether an auth failure means a session lapsed and the SSO session
 * behind it must be ended, recording the lapse for the login page when it does.
 *
 * The server expires the signed-in cookie on the first hop of the logout, so a boundary that fails
 * after the logout started would read it as a first visit; the latch keeps it on the logout path.
 * An expiry marker without the cookie means an earlier logout ended the application session but
 * never reached Authentik, so that logout is retried once. A logout whose landing page is not the
 * login page never consumes the marker, so a second retry would loop; it falls back to /login.
 */
export function beginSessionEndIfLapsed(redirectPath: string): boolean {
  if (isEndingSession) {
    return true
  }

  if (hasSignedInCookie()) {
    markSessionExpired(redirectPath)
    isEndingSession = true
  } else if (readExpiredSession() !== null) {
    if (claimLogoutRetry()) {
      isEndingSession = true
    } else {
      clearExpiredSession()
    }
  }

  return isEndingSession
}

/** Only tests need this; in a browser the logout navigation resets it by replacing the document. */
export function resetSessionEndLatch(): void {
  isEndingSession = false
}
